import {useRecoilState, useRecoilValue, useSetRecoilState} from "recoil";
import {useTranslation} from "react-i18next";
import {
    globalNotificationState,
    globalTwoFactorActionState,
    globalTwoFactorBusyState,
} from "./global_state";
import {requestTwoFactorChallengeMutation} from "../api/graphql/twoFactorChallenge";
import {createLog} from "../api/restful/logs/logs";
import {backendMessage} from "./backendMessage";

const REQUIRED_KEY = "admin.err.two_factor_required";

// a rule may demand a method the client has not enrolled in yet; the dialog offers to
// set it up where they stand, rather than sending them away from what they were doing
const SETUP_METHODS = {
    "admin.err.two_factor_passkey_setup_required": "passkey",
    "admin.err.two_factor_liveness_setup_required": "liveness",
    "admin.err.two_factor_telegram_setup_required": "telegram",
    "admin.err.two_factor_whatsapp_setup_required": "whatsapp",
};

// held outside react: the dialog is mounted once at the root, so whatever raised it is
// not the component that closes it
let pending = null;

// raising a challenge posts a mail, so a second click while one is in flight would
// otherwise leave the first caller waiting on a promise nothing resolves
let raising = false;

// read by the one mounted dialog
export const useTwoFactorAction = () => {
    const [challenge, setChallenge] = useRecoilState(globalTwoFactorActionState);

    const settle = (payload) => {
        const resolve = pending?.resolve;

        pending = null;
        setChallenge(null);
        resolve?.(payload);
    };

    return {
        challenge,
        onVerified: async (challengeId) => {
            const attempt = pending?.attempt;

            if (!attempt) {
                settle(null);
                return;
            }

            try {
                settle(await attempt(challengeId));
            } catch (error) {
                settle(null);
            }
        },
        onCancel: () => settle(null),
    };
};

// raising a challenge posts a mail and takes a moment, so whatever was clicked
// can say so instead of looking ignored
export const useTwoFactorBusy = () => useRecoilValue(globalTwoFactorBusyState);

// try the action first, challenge only if the brand guards it
const useTwoFactorGuard = () => {
    const {t} = useTranslation();
    const setNotificationMessage = useSetRecoilState(globalNotificationState);
    const setChallenge = useSetRecoilState(globalTwoFactorActionState);
    const setBusy = useSetRecoilState(globalTwoFactorBusyState);

    // resolves null when the client backs out
    const run = ({action, subAction = null, binding = null, attempt}) =>
        new Promise((resolve) => {
            (async () => {
                const payload = await attempt(null);

                if (payload?.errKey !== REQUIRED_KEY) {
                    resolve(payload);
                    return;
                }

                // the rejection names what the resolver will check on the retry, so the
                // challenge is asked for on those terms rather than the caller's guess
                const required = payload?.errObj || {};

                if (raising) {
                    resolve(null);
                    return;
                }

                raising = true;
                setBusy(true);

                let raised;

                try {
                    const response = await requestTwoFactorChallengeMutation({
                        action: required.action || action,
                        subAction: required.subAction ?? subAction,
                        binding: required.binding ?? binding,
                    });

                    raised = response?.requestTwoFactorChallenge;
                } finally {
                    raising = false;
                    setBusy(false);
                }

                if (!raised || raised?.__typename === "BaseError") {
                    const setupMethod = SETUP_METHODS[raised?.errKey];

                    if (setupMethod) {
                        setChallenge({setupRequired: setupMethod});
                        resolve(null);
                        return;
                    }

                    setNotificationMessage({
                        status: "error",
                        title: "",
                        msg: backendMessage(t, raised?.errKey),
                        key: "twoFactorGuardError",
                    });
                    resolve(null);
                    return;
                }

                pending = {attempt, resolve};
                setChallenge(raised);
            })().catch((error) => {
                // an unhandled throw here never resolves the promise the caller is
                // awaiting, so its finally never runs and the button spins forever
                console.log("Two factor guard failed:", error?.message);

                try {
                    createLog({
                        status: "error",
                        module: "TwoFactor",
                        submodule: "Guard",
                        function: "run",
                        description: `Guard threw: ${error?.name || "unknown"}`,
                        logData: {action, subAction, name: error?.name, message: error?.message},
                        triggerBy: "user",
                    });
                } catch (ignore) {}

                raising = false;
                setBusy(false);
                setNotificationMessage({
                    status: "error",
                    title: "",
                    msg: t("admin.err.system_error"),
                    key: "twoFactorGuardError",
                });
                resolve(null);
            });
        });

    return {run};
};

export default useTwoFactorGuard;
